import React, { useCallback, useEffect, useRef, useState } from "react";
import { isUserAuthorized } from "utils/login";
import InterpreterDesktopController from "./InterpreterDesktopController";
import {
  getInterpreterProvider,
  setURL,
} from "./providers/InterpreterProvider";
import { connect } from "react-redux";
import { useSelector, useDispatch } from "react-redux";
import {
  selectedInterpreterGender,
  interpreterLanguages,
  selectedInterpreterLanguage,
  selectedInterpreterVideoOption,
  setInterpreterRequestID,
  setInterpreterRequestStatus,
  interpreterGenderOption,
  interpreterCallTypeOption,
  setEstimatedTimeAvailable,
} from "store/actions/interpreter";
import { interpreterInviteSend } from "store/actions/googleAnalytics";
import { useTranslation } from "react-i18next";
import showNotification, {
  dismissNotification,
} from "components/Notifications/Notifications";
import interpreter_icon from "assets/images/buttons/ic_interpreter.svg";

const mapStateToProps = ({ config, call, user }, props) => ({
  customParameters: config.customParameters,
  urlPortal: config.urlPortal,
  urlRoomKey: config.urlRoomKey,
  gcpServices: config.listOfGCPServices,
  roomID: call.properties?.conferenceId,
  participantList: call.participants?.list,
  isRoomHasPin: user.userInfo?.RoomMode?.hasPIN,
  roomPin: user.userInfo?.RoomMode?.roomPIN,
  leaving: call.leaving,
});

const InterpreterDesktopWrapper = ({
  closeModal,
  urlPortal,
  urlRoomKey,
  gcpServices,
  roomID,
  isRoomHasPin,
  roomPin,
  leaving,
}) => {
  const { t } = useTranslation();
  const [interpreterRequestInProgress, setInterpreterRequestInProgress] =
    useState(
      useSelector((state) => state.interpreter.interpreterRequestIsInProgress)
    );
  const isSendAnalytics = useRef(false);
  const isInterpreterServiceAvailable = useRef(false);
  const [languageList, setLanguageList] = useState([]);
  const inviteNotification = useRef(null);
  const [genderOption, setgenderOption] = useState(
    useSelector((state) => state.interpreter.genderOptionList)
  );
  const [videoOption, setVideoOption] = useState(
    useSelector((state) => state.interpreter.callTypeList)
  );
  const [isGloboLanguageList, setIsGloboLanguageList] = useState(false);
  const favoriteLanguageList = useRef([]);
  const vendorLanguageList = useRef(
    useSelector((state) => state.interpreter.vendorLanguage)
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    useSelector((state) => state.interpreter.selectedLanguage)
  );
  const [selectedGender, setSelectedGender] = useState(
    useSelector((state) => state.interpreter.selectedGender)
  );
  const [selectedVideoOption, setSelectedVideoOption] = useState(
    useSelector((state) => state.interpreter.callType)
  );
  const isEstimatedTimeAvailable = useSelector(
    (state) => state.interpreter.isEstimatedTimeAvailable
  );
  const interpreterProvider = useRef({});
  const currentRequestId = useRef(
    useSelector((state) => state.interpreter.interpreterSessionID)
  );

  const dispatch = useDispatch();

  const setIsSendAnalytics = useCallback((isAllowed) => {
    isSendAnalytics.current = isAllowed;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (isUserAuthorized()) {
          console.error("User is guest");
          return;
        }
        if (
          !gcpServices?.interpreterService?.url ||
          !gcpServices?.interpreterService?.isServiceAvailable
        ) {
          console.error("listOfGCPServices interpreter configuration is wrong");
          return;
        }
        setURL(gcpServices?.interpreterService?.url);
        //setURL("https://interpreterservicev2-116393635455.us-central1.run.app/api/v1");
        intializeInterpreter();
        isInterpreterServiceAvailable.current = true;
      } catch (error) {
        console.error("initialization failed", error);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const intializeInterpreter = async () => {
    interpreterProvider.current = getInterpreterProvider();
    const interpreterConfig =
      await interpreterProvider.current.getConfiguration();
    setgenderOption(interpreterConfig.genderSelectionOption);
    dispatch(interpreterGenderOption(interpreterConfig.genderSelectionOption));
    setVideoOption(interpreterConfig.callTypeOptions);
    dispatch(interpreterCallTypeOption(interpreterConfig.callTypeOptions));
    dispatch(setEstimatedTimeAvailable(interpreterConfig.estimatedTime));
    setIsGloboLanguageList(interpreterConfig.globoLanguageList);
    if (vendorLanguageList.current.length <= 0) {
      vendorLanguageList.current = interpreterConfig.languageList;
      dispatch(interpreterLanguages(vendorLanguageList.current));
    }
    if (favoriteLanguageList.current.length === 0) {
      if (interpreterConfig.globoLanguageList) {
        let language =
          JSON.parse(localStorage.getItem("globoFavoriteLanguages")) || [];
        language?.sort(compareLanguage);
        favoriteLanguageList.current = language;
      } else {
        let language =
          JSON.parse(localStorage.getItem("favoriteLanguages")) || [];
        language?.sort(compareLanguage);
        favoriteLanguageList.current = language;
      }
    }

    vendorLanguageList.current.map((language) => {
      language.favorite = false;
      return language;
    });
    vendorLanguageList.current?.sort(compareLanguage);
    dispatch(interpreterLanguages(vendorLanguageList.current));

    for (const language of favoriteLanguageList.current) {
      vendorLanguageList.current = vendorLanguageList.current.filter(
        (vendorLanguageItem) => {
          return vendorLanguageItem.value !== language.value;
        }
      );
      dispatch(interpreterLanguages(vendorLanguageList.current));
    }

    setLanguageList([
      ...favoriteLanguageList.current,
      ...vendorLanguageList.current,
    ]);
  };

  const clearInfoNotification = () => {
    clearInviteNotification();
    clearRetryNotification();
  };

  const clearInviteNotification = () => {
    if (inviteNotification.current) {
      dismissNotification(inviteNotification.current);
      inviteNotification.current = null;
    }
  };
  const clearRetryNotification = () => {
    if (window.interpreterRetryInviteNotification) {
      dismissNotification(window.interpreterRetryInviteNotification);
      window.interpreterRetryInviteNotification = null;
    }
  };

  const retryInterpreterInviteNotification = (message = "") => {
    if (window.interpreterRetryInviteNotification) {
      return;
    }
    clearInviteNotification();
    window.interpreterRetryInviteNotification = showNotification(
      "bannerWithBtns",
      {
        type: "banner",
        header: t("INTERPRETER_REQUEST_FAILED"),
        message:
          message !== "" ? (
            <div className="message-content">
              <div className="message-content">{message}</div>
              <div className="message-content">{`${t(
                "PLEASE_TRY_AGAIN"
              )}`}</div>
            </div>
          ) : (
            <div class="message-content">${t("PLEASE_TRY_AGAIN")}</div>
          ),
        className:
          "with-button cc-error-banner interpreter-invite-request-notification",
        image: <img src={interpreter_icon} alt="" />,
        showFor: -1,
        buttons: [
          {
            text: t("RETRY"),
            className: "retry-interpreter-request",
            onClick: () => {
              sendInvite();
            },
          },
          {
            text: "",
            className: "close-interpreter-request",
            onClick: () => {
              clearInfoNotification();
            },
          },
        ],
      }
    );
  };

  const sendInvite = async () => {
    dispatch(setInterpreterRequestStatus(true));
    closeModal();
    const resp = await interpreterProvider.current.sendInvite({
      languageId: selectedLanguage.value,
      conferencType: selectedVideoOption,
      genderOption: selectedGender.value,
      meetingRoomId: urlRoomKey.value,
      meetingPortal: urlPortal.value,
      meetingRoomPin: isRoomHasPin ? roomPin : "",
    });
    if (resp.status) {
      currentRequestId.current = resp.RequestId;
      localStorage.setItem(
        "interpreterSessionId",
        JSON.stringify(currentRequestId.current)
      );
      localStorage.setItem("interpreterSessionRoomId", JSON.stringify(roomID));

      const GADATA = JSON.stringify({
        Language: selectedLanguage.text,
        Selected_Gender: t(selectedGender.text),
      });
      dispatch(interpreterInviteSend(GADATA));

      dispatch(setInterpreterRequestID(resp.RequestId));
      setInterpreterRequestInProgress(true);
      dispatch(setInterpreterRequestStatus(true));
      closeModal();
    } else {
      dispatch(setInterpreterRequestStatus(false));
      const Message = resp?.errorMessage || "";
      retryInterpreterInviteNotification(Message);
    }
  };

  const completeInvite = async () => {
    if (currentRequestId.current === null) {
      return;
    }
    await interpreterProvider.current.completeInviteRequest(
      currentRequestId.current
    );
    currentRequestId.current = null;
    setInterpreterRequestInProgress(false);
    dispatch(setInterpreterRequestStatus(false));
    dispatch(setInterpreterRequestID(null));
  };

  const favoriteSelected = (language, selected) => {
    if (selected) {
      vendorLanguageList.current = vendorLanguageList.current.filter(
        (vendorLanguageItem) => {
          return vendorLanguageItem.value !== language.value;
        }
      );
      dispatch(interpreterLanguages(vendorLanguageList.current));
      language.favorite = true;
      favoriteLanguageList.current.push(language);
      favoriteLanguageList.current?.sort(compareLanguage);
    } else {
      favoriteLanguageList.current = favoriteLanguageList.current?.filter(
        (favoriteLanguageItem) => {
          return favoriteLanguageItem.value !== language.value;
        }
      );
      language.favorite = false;
      vendorLanguageList.current.push(language);
      vendorLanguageList.current?.sort(compareLanguage);
      dispatch(selectedInterpreterLanguage(vendorLanguageList.current));
    }
    setLanguageList([
      ...favoriteLanguageList.current,
      ...vendorLanguageList.current,
    ]);
    if (isGloboLanguageList) {
      localStorage.setItem(
        "globoFavoriteLanguages",
        JSON.stringify(favoriteLanguageList.current)
      );
    } else {
      localStorage.setItem(
        "favoriteLanguages",
        JSON.stringify(favoriteLanguageList.current)
      );
    }
  };

  const languageSelected = (language) => {
    setSelectedLanguage({ ...language });
    dispatch(selectedInterpreterLanguage(language));
  };

  const videoOptionSelected = (videoOptionCode) => {
    setSelectedVideoOption(videoOptionCode);
    dispatch(selectedInterpreterVideoOption(videoOptionCode));
  };

  const genderOptionSelected = (genderOptionCode) => {
    setSelectedGender({ ...genderOptionCode });
    dispatch(selectedInterpreterGender(genderOptionCode));
  };

  function compareLanguage(a, b) {
    if (a.text < b.text) {
      return -1;
    }
    if (a.text > b.text) {
      return 1;
    }
    return 0;
  }
  return (
    <InterpreterDesktopController
      setIsSendAnalytics={setIsSendAnalytics}
      languageList={languageList}
      sendInvite={sendInvite}
      genderOption={genderOption}
      videoOption={videoOption}
      isEstimatedTimeAvailable={isEstimatedTimeAvailable}
      favoriteSelected={favoriteSelected}
      languageSelected={languageSelected}
      videoOptionSelected={videoOptionSelected}
      genderOptionSelected={genderOptionSelected}
      interpreterRequestInProgress={interpreterRequestInProgress}
      cancelInvite={completeInvite}
    />
  );
};

export default connect(mapStateToProps)(InterpreterDesktopWrapper);
